import { AUTH_MODE, type AuthConfig } from '@neotech-solutions-org/neofusion-fe-shared';
import { generateUrl, KEYCLOAK_CLIENT_ID } from '../utils/envDynamicValues';
import type { UserRole } from '../@types/api';
import { toSelectOption } from '../utils';
import { runtimeEnv } from '../config/runtimeEnv';

export const AUTH_CONFIG: AuthConfig = {
  mode: AUTH_MODE.KEYCLOAK,
  keycloak: {
    url: runtimeEnv.keycloakUrl ?? generateUrl('bo'),
    clientId: runtimeEnv.keycloakClientId ?? KEYCLOAK_CLIENT_ID,
    realm: runtimeEnv.keycloakRealm ?? 'sportsbook-bo-users',
  },
  initOptions: {
    onLoad: 'login-required',
  },
};
// TODO: clean unused permissions
export const PERMISSIONS = {
  accessCMS: 'access_cms',
  accessCampaigns: 'access_campaigns',
  accessCasino: 'access_casino',
  accessConditions: 'access_conditions',
  accessCRM: 'access_crm',
  accessLiabilityMonitoring: 'access_liability_monitoring',
  accessMarketplace: 'access_marketplace',
  accessMarketCombining: 'access_market_combining',
  accessPartners: 'access_partners',
  accessPaymentSettings: 'access_payment_settings',
  accessPlayers: 'access_players',
  accessProductReports: 'access_product_reports',
  accessPublishEvents: 'access_publish_events',
  accessReportsJackpot: 'access_reports_jackpot',
  accessRiskManagement: 'access_risk_management',
  accessSportsBetting: 'access_sports_betting',
  accessTicketAuthorization: 'access_ticket_authorization',
  accessTicketMonitoring: 'access_ticket_monitoring',
  accessUserManagement: 'access_user_management',
  accessWallet: 'access_wallet',
  addAdminCredits: 'add_admin_credits',
  assignAgentToManager: 'assign_agent_to_manager',
  assignPlayerToAgent: 'assign_player_to_agent',
  assignRiskFactors: 'assign_risk_factors',
  casinoCMS: 'access_casino_CMS',
  createAgent: 'create_agent',
  createAgentNote: 'create_agent_note',
  createAgentTask: 'create_agent_task',
  createManager: 'create_manager',
  createPlayer: 'create_player',
  createPlayerNote: 'create_player_note',
  createPlayerTask: 'create_player_task',
  defaultAdminRole: 'default-administrator-role',
  defaultAffiliateRole: 'default-affiliate-role',
  defaultAgentRole: 'default-agent-role',
  defaultManagerRole: 'default-manager-role',
  defaultSuperAdminRole: 'default-super-admin-role',
  defaultTechSupportRole: 'default-tech-support-role',
  defaultTraderRole: 'default-trader-role',
  editAgentDetails: 'edit_agent_details',
  editAssignedCustomers: 'edit_assigned_customers',
  editPartnerCommission: 'edit_partner_commission',
  editPlayerDetails: 'edit_player_details',
  impersonatePlayer: 'impersonate_player',
  manageAgentPassword: 'manage_agent_password',
  manageBettingSettings: 'manage_betting_settings',
  manageBonuses: 'manage_bonuses',
  manageCasinoCategories: 'manage_casino_categories',
  manageCasinoGames: 'manage_casino_games',
  manageCasinoProviders: 'manage_casino_providers',
  manageCashOut: 'manage_cash_out',
  manageEvents: 'manage_events',
  manageEventsResults: 'manage_events_results',
  manageJackpots: 'manage_jackpots',
  manageOffer: 'manage_offer',
  managePlayerPassword: 'manage_player_password',
  messageAgent: 'message_agent',
  messagePlayer: 'message_player',
  placeBetForPlayer: 'place_bet_for_player',
  setGlobalLiability: 'set_global_liability',
  setPlayerStatus: 'set_player_status',
  sportsbookCMS: 'access_sb_CMS',
  updateCustomerBalances: 'update_customer_balances',
  updateCustomerStatus: 'update_customer_status',
  viewAgentsReports: 'view_agents_reports',
  viewAgentTransactions: 'view_agent_transactions',
  viewAffiliatesReports: 'view_affiliates_reports',
  viewAssignedCustomers: 'view_assigned_customers',
  viewBonusReports: 'view_bonus_reports',
  viewCasinoBets: 'view_casino_bets',
  viewCasinoReports: 'view_casino_reports',
  viewCasinoTransactions: 'view_casino_transactions',
  viewCasinoUsers: 'view_casino_users',
  viewCashoutReports: 'view_cashout_reports',
  viewBetslipDetails: 'view_betslip_details',
  viewCustomerBalances: 'view_customer_balances',
  viewDailyReports: 'view_daily_reports',
  viewManagersReports: 'view_managers_reports',
  viewManagerTransactions: 'view_manager_transactions',
  viewPartnersReports: 'view_partners_reports',
  viewPaymentReports: 'view_payment_reports',
  viewNgrReportColumn: 'view_ngr_report_column',
  viewPlayersReports: 'view_players_reports',
  viewSportsbookReports: 'view_sportsbook_reports',
};

export const USER_ROLES = {
  SUPER_ADMIN: 'Super Administrator',
  ADMIN: 'Administrator',
  MANAGER: 'Manager',
  AGENT: 'Agent',
  AFFILIATE: 'Affiliate',
  TRADER: 'Trader',
  TECH_SUPPORT: 'Technical Support',
} as const;

type RoleConfig = {
  requiresManager: boolean;
  hasWallet: boolean;
  canManage: UserRole[];
  defaultRoute: string;
  standaloneCasinoRoute: string;
};

const ROLE_CONFIG: Record<UserRole, RoleConfig> = {
  [USER_ROLES.SUPER_ADMIN]: {
    requiresManager: false,
    hasWallet: true,
    canManage: [
      USER_ROLES.ADMIN,
      USER_ROLES.MANAGER,
      USER_ROLES.AGENT,
      USER_ROLES.AFFILIATE,
      USER_ROLES.TECH_SUPPORT,
      USER_ROLES.TRADER,
    ],
    defaultRoute: '/marketplace',
    standaloneCasinoRoute: '/marketplace',
  },
  [USER_ROLES.ADMIN]: {
    requiresManager: false,
    hasWallet: true,
    canManage: [USER_ROLES.MANAGER, USER_ROLES.AGENT, USER_ROLES.AFFILIATE, USER_ROLES.TECH_SUPPORT, USER_ROLES.TRADER],
    defaultRoute: '/sports-betting/settings/sports',
    standaloneCasinoRoute: '/casino',
  },
  [USER_ROLES.MANAGER]: {
    requiresManager: false,
    hasWallet: true,
    canManage: [USER_ROLES.AGENT],
    defaultRoute: '/crm/partners',
    standaloneCasinoRoute: '/crm/partners',
  },
  [USER_ROLES.AGENT]: {
    requiresManager: true,
    hasWallet: true,
    canManage: [],
    defaultRoute: '/crm/players',
    standaloneCasinoRoute: '/crm/players',
  },
  [USER_ROLES.AFFILIATE]: {
    requiresManager: true,
    hasWallet: false,
    canManage: [],
    defaultRoute: '/crm/players',
    standaloneCasinoRoute: '/crm/players',
  },
  [USER_ROLES.TECH_SUPPORT]: {
    requiresManager: false,
    hasWallet: false,
    canManage: [],
    defaultRoute: '/sports-betting/settings/sports',
    standaloneCasinoRoute: '/casino',
  },
  [USER_ROLES.TRADER]: {
    requiresManager: false,
    hasWallet: false,
    canManage: [],
    defaultRoute: '/sports-betting/offer-management/events',
    standaloneCasinoRoute: '/casino',
  },
};

export const getRoleOptions = (role: UserRole) => (ROLE_CONFIG[role]?.canManage ?? []).map(toSelectOption);
export const roleRequiresManager = (role?: UserRole) => !!(role && ROLE_CONFIG[role]?.requiresManager);
export const roleHasWallet = (role?: UserRole) => !!(role && ROLE_CONFIG[role]?.hasWallet);
export const getDefaultRoute = (role: UserRole, isStandaloneCasino: boolean) =>
  isStandaloneCasino ? ROLE_CONFIG[role]?.standaloneCasinoRoute : ROLE_CONFIG[role]?.defaultRoute;
